import React, { useEffect, useState, PropsWithChildren, useContext } from "react";
import log from "loglevel";
import _ from "lodash";
import * as PublicFolderApi from "../api/PublicFolderApi";
import { MiniSuomiAppInfo } from "../models/AppModels";

export enum AppStateProperty {
  MINISUOMIAPPINFOS = "miniSuomiAppInfos",
}

export enum AppStatePropertyState {
  UNINITIALIZED = "uninitialized",
  INITIALIZED = "initialized",
  ERROR = "error"
}

const setDefaultPropStates = () => {
  const states: {[id: string]: AppStatePropertyState} = {};
  Object.entries(AppStateProperty).forEach(entry => {
    states[`${entry[1]}`] = AppStatePropertyState.UNINITIALIZED;
  });
  return states;
}

interface AppState {
  isLoadingApp: boolean,
  loggedIn: boolean,
  miniSuomiAppInfos: {[lang: string]: MiniSuomiAppInfo[]},
  propertyStates: {[id: string]: AppStatePropertyState}
}

export type AppStateType = AppState & {
  // Functions
  refreshAppState: () => Promise<void>,
  resetAppState: () => Promise<void>,
  logout: () => Promise<void>,
}

export const DefaultAppState: AppState = {
  isLoadingApp: true,
  loggedIn: false,
  miniSuomiAppInfos: {},
  propertyStates: setDefaultPropStates()
}

export const DefaultAppStateContext: AppStateType = {
  ...DefaultAppState,
  refreshAppState: () => new Promise<void>(resolve => resolve()),
  resetAppState: () => new Promise<void>(resolve => resolve()),
  logout: () => new Promise<void>(resolve => resolve()),
}

// AppContext with default values. AppContextProvider replaces defaults with the real values.
export const AppStateContext = React.createContext<AppStateType>(DefaultAppStateContext);

const AppContextProvider: React.FC<PropsWithChildren> = ({children}) => {
  const [appState, setAppState] = useState<AppState>(DefaultAppState);
  // Contains ongoing request promises. To update state use methods addPromise and removePromise
  const [promises, setPromises] = useState<{[type: string]: Promise<any>}>({});
  const logger = log.getLogger(AppContextProvider.name);

  // private
  const addPromise = (type: string, promise: Promise<any>) => {
    setPromises(oldState => ({...oldState, [type]: promise}));
  }

  // private
  const removePromise = (type: string) => {
    setPromises(oldState => _.omit(oldState, type));
  }

  // Run this useEffect only once when app loads
  useEffect(() => {
    loadAppStateAsync();
  }, []);
  
  // private
  const loadAppStateAsync = async (): Promise<void> => {
    logger.debug("ACP loadAppStateAsync");
    setAppState(appState => ({...appState, isLoadingApp: true}));
    loadMiniSuomiAppInfosAsync()
    .then(() => setAppState(appState => ({...appState, isLoadingApp: false})))
    .catch(err => {
      logger.error("loadAppStateAsync failed", err);
      setAppState(appState => ({...appState, isLoadingApp: false}));
    })
  }

  const loadMiniSuomiAppInfosAsync = async (): Promise<void> => {
    logger.debug("loadMiniSuomiAppInfosAsync");
    const infosFi = getMiniSuomiAppInfosAsync("fi");
    const infosEn = getMiniSuomiAppInfosAsync("en");
    return Promise.all([infosFi, infosEn])
    .then(res => {
      logger.debug("loadMiniSuomiAppInfosAsync done");
    })
  }

  const getMiniSuomiAppInfosAsync = async (lang: string): Promise<MiniSuomiAppInfo[]> => {
    if (appState.miniSuomiAppInfos[lang]) {
      return appState.miniSuomiAppInfos[lang];
    }
    const AppStateProp = AppStateProperty.MINISUOMIAPPINFOS;
    const promiseKey = `${AppStateProperty.MINISUOMIAPPINFOS}-${lang}`;
    return promises[promiseKey] ?? (
      async (langKey: string): Promise<MiniSuomiAppInfo[]> => {
        let propState = AppStatePropertyState.INITIALIZED;
        const promise = PublicFolderApi.GetFile<MiniSuomiAppInfo[]>(`data/MiniSuomiApps-${langKey}.json`)
        .then(res => {
          if (res.data) {
            setAppState(oldState => ({...oldState, miniSuomiAppInfos: {...oldState.miniSuomiAppInfos, [langKey]: res.data}}));
            return res.data;
          }
          logger.debug("ACP getMiniSuomiAppInfosAsync response without miniSuomiAppInfos", res);
          propState = AppStatePropertyState.ERROR;
          return [];
        })
        .catch(err => {
          logger.debug("ACP getMiniSuomiAppInfosAsync catched an error", err);
          propState = AppStatePropertyState.ERROR;
          return [];
        })
        .finally(() => {
          removePromise(promiseKey);
          setAppState(oldState => ({...oldState, propertyStates: {...oldState.propertyStates, [AppStateProp]: propState}}));
        });
        addPromise(promiseKey, promise);
        return promise;
      }
    )(lang);
  }

  const refreshAppState = async (): Promise<void> => {
    await loadAppStateAsync();
  }

  const resetAppState = async (): Promise<void> => {
    setAppState(() => DefaultAppState);
  }

  const logout = async (): Promise<void> => {
    return resetAppState()
    .then(() => {
      return loadAppStateAsync();
    });
  }

  return (
    <AppStateContext.Provider value={{
      ...appState,
      refreshAppState,
      resetAppState,
      logout
    }}>
      {children}
    </AppStateContext.Provider>
  );
}

export const useAppStateContext = () => {
  const appContext = useContext(AppStateContext);
  if (appContext === undefined) {
    throw new Error("useAppStateContext must be used within a AppContextProvider");
  }
  return appContext;
}

export default AppContextProvider;